import { LANGUAGE_PREFERENCE_KEY, explicitLanguage, type AppLanguage } from '@anidex/shared/language';
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import enCatalog from './locales/en/catalog.json';
import enCollection from './locales/en/collection.json';
import enCommon from './locales/en/common.json';
import enErrors from './locales/en/errors.json';
import enLegal from './locales/en/legal.json';
import enMarket from './locales/en/market.json';
import enPages from './locales/en/pages.json';
import enPlay from './locales/en/play.json';
import enSocial from './locales/en/social.json';
import enAdmin from './locales/en/admin.json';
import frCatalog from './locales/fr/catalog.json';
import frCollection from './locales/fr/collection.json';
import frCommon from './locales/fr/common.json';
import frErrors from './locales/fr/errors.json';
import frLegal from './locales/fr/legal.json';
import frMarket from './locales/fr/market.json';
import frPages from './locales/fr/pages.json';
import frPlay from './locales/fr/play.json';
import frSocial from './locales/fr/social.json';
import frAdmin from './locales/fr/admin.json';
import frPremium from './locales/fr/premium.json';
import enPremium from './locales/en/premium.json';
import frBilling from './locales/fr/billing.json';
import enBilling from './locales/en/billing.json';

export const LANGUAGE_STORAGE_KEY = LANGUAGE_PREFERENCE_KEY;
export type { AppLanguage };


function readExplicitLanguage(): AppLanguage | null {
  try {
    return explicitLanguage(localStorage.getItem(LANGUAGE_STORAGE_KEY));
  } catch {
    return null;
  }
}


function documentLanguage(): AppLanguage | null {
  if (typeof document === 'undefined') return null;
  return explicitLanguage(document.documentElement.lang);
}

function initialLanguage(): AppLanguage {
  return readExplicitLanguage() ?? documentLanguage() ?? 'fr';
}

function writeLanguageCookie(language: AppLanguage) {
  if (typeof document === 'undefined') return;
  const secure = location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${LANGUAGE_PREFERENCE_KEY}=${language}; Path=/; Max-Age=31536000; SameSite=Lax${secure}`;
}

void i18n.use(initReactI18next).init({
  resources: {
    fr: {
      common: frCommon,
      catalog: frCatalog,
      errors: frErrors,
      pages: frPages,
      play: frPlay,
      collection: frCollection,
      market: frMarket,
      social: frSocial,
      legal: frLegal,
      admin: frAdmin,
      premium: frPremium,
      billing: frBilling,
    },
    en: {
      common: enCommon,
      catalog: enCatalog,
      errors: enErrors,
      pages: enPages,
      play: enPlay,
      collection: enCollection,
      market: enMarket,
      social: enSocial,
      legal: enLegal,
      admin: enAdmin,
      premium: enPremium,
      billing: enBilling,
    },
  },
  lng: initialLanguage(),
  fallbackLng: 'fr',
  supportedLngs: ['fr', 'en'],
  defaultNS: 'common',
  ns: ['common', 'catalog', 'errors', 'pages', 'play', 'collection', 'market', 'social', 'legal', 'admin', 'premium', 'billing'],
  interpolation: { escapeValue: false },
  returnNull: false,
});

export function appLanguage(): AppLanguage {
  return i18n.language.startsWith('en') ? 'en' : 'fr';
}

export function intlLocale(): string {
  return appLanguage() === 'en' ? 'en-US' : 'fr-FR';
}

function applyDocumentLanguage(language: string) {
  if (typeof document === 'undefined') return;
  document.documentElement.lang = language.startsWith('en') ? 'en' : 'fr';
}

applyDocumentLanguage(i18n.language);
i18n.on('languageChanged', applyDocumentLanguage);

const explicitOnBoot = readExplicitLanguage();
if (explicitOnBoot) writeLanguageCookie(explicitOnBoot);

export function setLanguage(language: AppLanguage) {
  try {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
  } catch {
    
  }
  writeLanguageCookie(language);
  void i18n.changeLanguage(language);
}

export default i18n;
